import { useEffect, useMemo, useRef, useState } from 'react'
import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { cn } from '@/shared/utils/cn'
import { useScope } from './ScopeProvider'
import {
  documentDisplayName,
  documentKey,
  isDocumentRetrievable,
  unavailableReason,
} from './scopeUtils'

interface Props {
  open: boolean
  onClose: () => void
  /** Optional anchor — clicks on this element are not treated as outside. */
  anchorRef?: React.RefObject<HTMLElement | null>
}

/**
 * Popover-style scope picker. Lists the tenant's indexed case documents and
 * lets the user attach one OR many to the next conversation (Step 8.3 —
 * Multi-document Case Scope). Documents without chunks or with a non-
 * indexed status are rendered disabled with a hint instead of being hidden,
 * so the user understands why retrieval would fail.
 *
 * UX rules:
 *   - "Todo o workspace" sits at the top and clears any selection.
 *   - Each row toggles in/out of the multi-doc selection on click.
 *   - The footer surfaces the active selected count, so the user always
 *     knows whether 0, 1 or N case documents will be sent on the next
 *     question.
 */
export function ScopeSelector({ open, onClose, anchorRef }: Props) {
  const {
    documents,
    status,
    error,
    selectedCaseDocuments,
    reload,
    toggleCaseDocument,
    clearCaseDocuments,
  } = useScope()
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const root = containerRef.current
      if (!root) return
      const target = e.target as Node
      if (root.contains(target)) return
      if (anchorRef?.current?.contains(target)) return
      onClose()
    }
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', keyHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', keyHandler)
    }
  }, [open, onClose, anchorRef])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return documents
    return documents.filter((d) => {
      const hay = [
        d.document_id,
        d.filename ?? '',
        d.entity_id ?? '',
        d.entity_type ?? '',
        d.group_id ?? '',
        d.group_type ?? '',
      ]
        .join(' ')
        .toLowerCase()
      return hay.includes(needle)
    })
  }, [documents, query])

  // Memoize the active selection as a Set of stable keys for O(1) lookup.
  const selectedKeys = useMemo(
    () => new Set(selectedCaseDocuments.map((d) => documentKey(d))),
    [selectedCaseDocuments],
  )

  if (!open) return null

  const selectedCount = selectedCaseDocuments.length

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Selecionar escopo do RAG"
      data-testid="scope-selector"
      className="absolute bottom-[calc(100%+10px)] left-0 z-30 w-[min(540px,calc(100vw-32px))] rounded-2xl border border-white/10 bg-[rgba(15,16,22,0.96)] shadow-[0_30px_80px_-30px_rgba(124,92,255,0.55)] backdrop-blur-xl"
    >
      <header className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-text">
          <Icon name="grid" size={14} className="text-violet" />
          Escopo do RAG
          {selectedCount > 0 && (
            <span
              className="ml-1 rounded-md border border-violet/40 bg-violet/[0.18] px-1.5 py-0.5 font-mono text-[10px] text-violet"
              aria-label={`${selectedCount} documento${selectedCount === 1 ? '' : 's'} selecionado${selectedCount === 1 ? '' : 's'}`}
            >
              {selectedCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="grid h-7 w-7 place-items-center rounded-lg text-text-mute hover:bg-white/[0.06] hover:text-text"
          title="Fechar"
          aria-label="Fechar"
        >
          <Icon name="close" size={14} />
        </button>
      </header>

      <div className="px-4 py-3">
        <p className="m-0 text-[11.5px] leading-[1.55] text-text-mute">
          Selecione um ou mais documentos de caso para limitar a recuperação,
          ou mantenha "Todo o workspace" para deixar todo o tenant ativo.
        </p>

        <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2">
          <Icon name="search" size={13} className="text-text-mute" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Prevent the popover's parent <form> (composer) from
                // accidentally submitting when the user just wanted to search.
                e.preventDefault()
              }
            }}
            placeholder="Buscar por paciente, documento, grupo…"
            className="w-full bg-transparent text-[12.5px] text-text placeholder:text-text-dim focus:outline-none"
            aria-label="Buscar documento"
          />
        </div>
      </div>

      <div className="max-h-[300px] overflow-y-auto px-2 pb-2">
        <ScopeAllOption
          active={selectedCount === 0}
          onSelect={() => {
            clearCaseDocuments()
          }}
        />

        {status === 'loading' && (
          <div className="px-3 py-6 text-center text-[12px] text-text-mute">
            Carregando catálogo…
          </div>
        )}

        {status === 'error' && (
          <div className="mx-2 my-2 rounded-lg border border-red-400/30 bg-red-500/[0.07] px-3 py-2 text-[12px] text-red-200">
            {error ?? 'Falha ao carregar o catálogo.'}{' '}
            <button
              type="button"
              onClick={() => void reload()}
              className="ml-1 underline hover:text-red-100"
            >
              tentar novamente
            </button>
          </div>
        )}

        {status === 'ready' && filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-text-mute">
            {documents.length === 0
              ? 'Nenhum documento indexado neste workspace ainda.'
              : 'Nenhum documento corresponde à busca.'}
          </div>
        )}

        {filtered.map((doc) => {
          const key = documentKey(doc)
          const isActive = selectedKeys.has(key)
          return (
            <DocumentRow
              key={key}
              doc={doc}
              active={isActive}
              onToggle={() => {
                toggleCaseDocument(doc)
              }}
            />
          )
        })}
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-white/[0.06] px-4 py-2 text-[10.5px] uppercase tracking-[0.12em] text-text-mute">
        <span>
          {selectedCount === 0
            ? `Total: ${documents.length} doc${documents.length === 1 ? '' : 's'}`
            : `${selectedCount} selecionado${selectedCount === 1 ? '' : 's'} · ${documents.length} doc${documents.length === 1 ? '' : 's'}`}
        </span>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={() => {
              clearCaseDocuments()
            }}
            className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-medium normal-case tracking-normal text-text-mute hover:border-white/20 hover:bg-white/[0.06] hover:text-text"
            title="Limpar seleção"
          >
            Limpar
          </button>
        )}
      </footer>
    </div>
  )
}

function ScopeAllOption({
  active,
  onSelect,
}: {
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'mt-1 flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-[background-color,border-color] duration-150 ease-smooth hover:border-white/10 hover:bg-white/[0.04]',
        active && 'border-violet/40 bg-violet/[0.08]',
      )}
    >
      <span
        className={cn(
          'grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg border',
          active
            ? 'border-violet/40 bg-violet/[0.18] text-violet'
            : 'border-white/10 bg-white/[0.04] text-text-mute',
        )}
        aria-hidden="true"
      >
        <Icon name="grid" size={14} />
      </span>
      <span className="flex-1">
        <span className="block text-[12.5px] font-medium text-text">
          Todo o workspace
        </span>
        <span className="block text-[11px] text-text-mute">
          Sem escopo específico — RAG busca em todos os documentos indexados.
        </span>
      </span>
      {active && (
        <span className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-violet">
          Ativo
        </span>
      )}
    </button>
  )
}

function DocumentRow({
  doc,
  active,
  onToggle,
}: {
  doc: CatalogDocumentDTO
  active: boolean
  onToggle: () => void
}) {
  const unavailable = unavailableReason(doc)
  const disabled = !isDocumentRetrievable(doc)
  const subtitleParts = [
    doc.entity_type && doc.entity_id
      ? `${doc.entity_type}: ${doc.entity_id}`
      : null,
    doc.group_id ? `${doc.group_type}: ${doc.group_id}` : null,
  ].filter(Boolean) as string[]

  return (
    <button
      type="button"
      data-testid="scope-doc-row"
      data-document-id={doc.document_id}
      onClick={() => {
        if (disabled) return
        onToggle()
      }}
      disabled={disabled}
      aria-pressed={active}
      title={unavailable ?? `Selecionar ${documentDisplayName(doc)}`}
      className={cn(
        'mt-1 flex w-full items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-[background-color,border-color] duration-150 ease-smooth',
        !disabled && 'hover:border-white/10 hover:bg-white/[0.04]',
        active && 'border-violet/40 bg-violet/[0.08]',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg border',
          active
            ? 'border-violet/40 bg-violet/[0.18] text-violet'
            : 'border-white/10 bg-white/[0.04] text-text-mute',
        )}
        aria-hidden="true"
      >
        <Icon name={active ? 'check' : 'doc'} size={14} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-[12.5px] font-medium text-text">
          {documentDisplayName(doc)}
        </span>
        <span className="block truncate text-[11px] text-text-mute">
          {subtitleParts.join(' · ')}
          {subtitleParts.length > 0 && ' · '}
          <span className="font-mono text-[10.5px] text-text-dim">
            {doc.document_id}
          </span>
        </span>
        {unavailable && (
          <span className="mt-1 block text-[10.5px] text-amber-200/80">
            {unavailable}
          </span>
        )}
      </span>
      <span className="flex flex-col items-end gap-1">
        <span className="rounded-md border border-white/[0.08] bg-white/[0.03] px-1.5 py-0.5 font-mono text-[10px] text-text-mute">
          {doc.chunk_count} chunk{doc.chunk_count === 1 ? '' : 's'}
        </span>
        {active && (
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-violet">
            Ativo
          </span>
        )}
      </span>
    </button>
  )
}
